/* eslint-disable @typescript-eslint/no-explicit-any */
const JBL_GROUP_TYPE = "3r"

type UseJBLWebSocketCoreParams = {
  isLogin: Ref<boolean>
  defaultChangeLines: Ref<string[]>
  loginedChangeLines: Ref<string[]>
  ENABLE_JBL_INTELLIGENT_CHANGE_LINE: Ref<boolean>
  originAPIUrl: Ref<string>
}

type Group = {
  result: Record<string, string>
  time: number
}

const formattedChangeLines = (APIUrl: string, lines: string[]) => {
  return lines.map((line: string) => {
    const baseURL = APIUrl.replace(/^[^.]*/, line)
    return baseURL
  })
}

const useJBLWebSocketCore = ({
  isLogin,
  defaultChangeLines,
  loginedChangeLines,
  ENABLE_JBL_INTELLIGENT_CHANGE_LINE,
  originAPIUrl
}: UseJBLWebSocketCoreParams) => {
  const JBL = ref<any>(null)

  const groupName = ref({
    login: "",
    notLogin: ""
  })

  const group = ref<Group>({
    result: {},
    time: 0
  })

  const webSiteInfoStore = useWebsiteInfoStore()
  const { state: websiteInfo } = storeToRefs(webSiteInfoStore)

  const dC = computed(() =>
    formattedChangeLines(originAPIUrl.value, defaultChangeLines.value)
  )
  const qD = computed(() =>
    formattedChangeLines(originAPIUrl.value, loginedChangeLines.value)
  )

  const groupLinesWithoutLogin = computed(() => dC.value.join(","))
  const groupLinesForLogin = computed(() => qD.value.join(","))

  const shouldLoadJBLScript = computed(() => {
    return ENABLE_JBL_INTELLIGENT_CHANGE_LINE.value
  })

  const hasMountedJBL = computed(() => JBL.value !== null)

  const getGroupResult = async () => {
    // get group result first, and wait for websocket to update data
    const selectedGroupName = isLogin.value
      ? groupName.value.login
      : groupName.value.notLogin
    group.value = await JBL.value?.getGroupResult(JBL_GROUP_TYPE, selectedGroupName)
  }

  const initJBLWebSocket = async (webSiteDomain: string) => {
    groupName.value.login = `qD_${webSiteDomain}`
    groupName.value.notLogin = `dC_${webSiteDomain}`

    await JBL.value?.connect(websiteInfo.value.jblHashCode, "XXX")

    JBL.value?.testLine(JBL_GROUP_TYPE, groupName.value.login, groupLinesForLogin.value)
    JBL.value?.testLine(
      JBL_GROUP_TYPE,
      groupName.value.notLogin,
      groupLinesWithoutLogin.value
    )

    await getGroupResult()
  }

  const loadJBLScript = async () => {
    const scriptDomain = import.meta.env.VITE_HOST_URL ?? ""

    if (typeof window !== "undefined" && !("JBLWebSocket" in window)) {
      console.debug("Loading JBL WebSocket script")
      await import(/* @vite-ignore */ `${scriptDomain}/plugins/jbl.js`)
    }

    if ("JBLWebSocket" in window) {
      JBL.value = window.JBLWebSocket as any
    }
  }

  const fetchRecommendLine = async () => {
    if (!JBL.value) {
      console.debug("JBL WebSocket is not ready yet")
      return ""
    }

    if (isLogin.value) {
      console.debug("Getting recommended line for logged-in user", group.value?.result)
      return group.value?.result?.[`${JBL_GROUP_TYPE}_${groupName.value.login}`] || ""
    }

    console.debug("Getting recommended line for guest user", group.value?.result)
    return group.value?.result?.[`${JBL_GROUP_TYPE}_${groupName.value.notLogin}`] || ""
  }

  onUnmounted(() => {
    JBL.value?.remove()
  })

  return {
    shouldLoadJBLScript,
    hasMountedJBL,
    loadJBLScript,
    initJBLWebSocket,
    fetchRecommendLine
  }
}

export default useJBLWebSocketCore
